import { SyncStatusBadge } from "@nexxt/app-kit";
import type { CreditOutcome, SoLotStatus, SoStatus } from "@nexxt/contracts/sales";
import { StatusBadge } from "@nexxt/ui";
import { creditOutcomeTone, soLotStatusTone, soStatusTone } from "@nexxt/ui/status";
import { m } from "../../../paraglide/messages.js";
import type { SalesOrder } from "./sales-order.queries";

// Badge texts are sentences from the catalog, never enum values (design-system.md §2.2).

export const soStatusLabel: Record<SoStatus, () => string> = {
  confirmed: m.so_status_confirmed,
  credit_blocked: m.so_status_credit_blocked,
  allocated: m.so_status_allocated,
  locked: m.so_status_locked,
  awaiting_payment: m.so_status_awaiting_payment,
  document_requested: m.so_status_document_requested,
  document_error: m.so_status_document_error,
  document_issued: m.so_status_document_issued,
  released_to_3pl: m.so_status_released_to_3pl,
  completed: m.so_status_completed,
  cancelled: m.so_status_cancelled,
};

export function SoStatusBadge({ status }: { status: SoStatus }) {
  return <StatusBadge tone={soStatusTone[status]} label={soStatusLabel[status]()} />;
}

/** A lot moves through the statuses of an order from its lock on (ADR-195), in the same words; then it is shipped. */
export const lotStatusLabel: Record<SoLotStatus, () => string> = {
  locked: m.so_status_locked,
  awaiting_payment: m.so_status_awaiting_payment,
  document_requested: m.so_status_document_requested,
  document_error: m.so_status_document_error,
  document_issued: m.so_status_document_issued,
  released_to_3pl: m.so_status_released_to_3pl,
  awaiting_invoice: m.so_lot_status_awaiting_invoice,
  shipped: m.so_lot_status_shipped,
  cancelled: m.so_status_cancelled,
};

export function LotStatusBadge({ status }: { status: SoLotStatus }) {
  return <StatusBadge tone={soLotStatusTone[status]} label={lotStatusLabel[status]()} />;
}

export const creditLabel: Record<CreditOutcome, () => string> = {
  pending: m.so_credit_pending,
  approved: m.so_credit_approved,
  blocked: m.so_credit_blocked,
  override_approved: m.so_credit_override_approved,
};

/** The credit of an order as a badge, for the details: in the lists the status already says a block before allocation. */
export function CreditOutcomeBadge({ outcome }: { outcome: CreditOutcome }) {
  return <StatusBadge tone={creditOutcomeTone[outcome]} label={creditLabel[outcome]()} />;
}

/**
 * Business Central knows an order from its lock on (I-03): before, there is nothing to say. After, the state is said
 * as for a PO: same words, same tones.
 */
export function SoSyncStatusBadge({ so }: { so: Pick<SalesOrder, "bcSyncStatus"> }) {
  return so.bcSyncStatus === "not_synced" ? null : <SyncStatusBadge status={so.bcSyncStatus} />;
}

/**
 * What holds an order besides its status: a credit block found after the allocation (Q-SAL-05), which the status does
 * not show, and a change of Joor that waits for the Customer Service (B-31).
 */
export function SoHoldBadges({ so }: { so: Pick<SalesOrder, "status" | "creditOutcome" | "joorChangePending"> }) {
  return (
    <>
      {so.creditOutcome === "blocked" && so.status !== "credit_blocked" && <CreditOutcomeBadge outcome="blocked" />}
      {so.joorChangePending && <StatusBadge tone="warning" label={m.so_joor_change_pending()} />}
    </>
  );
}

/** The states of an order side by side: its status, what holds it, Business Central. */
export function SalesOrderBadges({
  so,
}: {
  so: Pick<SalesOrder, "status" | "creditOutcome" | "joorChangePending" | "bcSyncStatus">;
}) {
  return (
    <>
      <SoStatusBadge status={so.status} />
      <SoHoldBadges so={so} />
      <SoSyncStatusBadge so={so} />
    </>
  );
}
